import { Button } from '@/components/ui/primitives';
import {
    MarketingPage,
    Hero,
    ContextNote,
    FeatureGrid,
    CtaBand,
    PageFooter,
    Dateline,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';
import { WHATS_NEW_ENTRIES } from '@/content/whats-new';

const PAGE = pageForRoute('/whats-new');

/** Format a YYYY-MM-DD date as "Month YYYY" for section headings. */
function monthLabel(isoDate: string): string {
    const [year, month] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });
}

/** Format a YYYY-MM-DD date as "Month D, YYYY" for card datelines. */
function dateLabel(isoDate: string): string {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

/**
 * Group entries by calendar month (YYYY-MM), preserving insertion order.
 * Entries must already be sorted newest-first.
 */
function groupByMonth(entries: typeof WHATS_NEW_ENTRIES) {
    const groups = new Map<string, typeof WHATS_NEW_ENTRIES[number][]>();
    for (const entry of entries) {
        const key = entry.date.slice(0, 7); // "YYYY-MM"
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(entry);
    }
    return Array.from(groups.entries()).map(([, items]) => items);
}

const MONTH_GROUPS = groupByMonth(WHATS_NEW_ENTRIES);

export function WhatsNewPage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="What's New"
                    title={<>What's new in PDF Markup Tool.</>}
                    lead="A dated record of new features and fixes -- photo markup, dimension measurement, Apple Pencil improvements, and more. Newest first."
                    cta={<Button href="/" variant="primary" size="lg">Open PDF Markup Tool</Button>}
                    chips={['No upload', 'No account', 'Free'] as const}
                />
                {MONTH_GROUPS.map((group) => (
                    <FeatureGrid
                        key={group[0].date.slice(0, 7)}
                        sectionTitle={monthLabel(group[0].date)}
                        sectionLead=""
                        cards={group.map((entry) => ({
                            title: entry.title,
                            body: `${dateLabel(entry.date)} -- ${entry.description}`,
                        }))}
                    />
                ))}
                <ContextNote text="PDF Markup Tool runs entirely in your browser. No file is uploaded and no account is required." />
                <CtaBand
                    title="Try the latest version now"
                    lead="Everything on this page is live in the browser. No install, no signup."
                    actions={<><Button href="/" variant="primary" size="lg">Open PDF Markup Tool</Button><Button href="/redline-pdf-drawing/" variant="ghost" size="lg">How to redline a drawing</Button></>}
                />
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="Free browser-native PDF markup for construction drawing review" />
        </MarketingPage>
    );
}
